import { createContext, useContext, useRef, useState, useCallback } from "react";
import { Radio, Pause } from "lucide-react";

const CommentaryContext = createContext({ playing: false, play: () => {}, stop: () => {}, toggle: () => {} });

export const useCommentary = () => useContext(CommentaryContext);

export const CommentaryProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const play = useCallback((startAt) => {
    const a = audioRef.current;
    if (!a) return;
    if (typeof startAt === "number" && !Number.isNaN(startAt) && isFinite(startAt)) {
      try { a.currentTime = startAt; } catch (e) { /* noop */ }
    }
    a.play().then(() => setPlaying(true)).catch(() => {});
  }, []);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); setPlaying(false); }
  }, []);

  const toggle = useCallback(() => {
    if (playing) stop(); else play();
  }, [playing, play, stop]);

  return (
    <CommentaryContext.Provider value={{ playing, play, stop, toggle, audioRef }}>
      <audio ref={audioRef} src="/commentary.mp3" preload="none" onEnded={() => setPlaying(false)} />
      {children}
      {playing && (
        <div
          data-testid="commentary-miniplayer"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 bg-ink text-ivory pl-5 pr-2 py-2 rounded-full shadow-2xl shadow-black/40"
        >
          <Radio size={16} className="text-windsor animate-pulse" />
          <span className="text-sm font-medium">Pete's commentary is playing</span>
          <button
            onClick={stop}
            data-testid="commentary-miniplayer-stop"
            className="grid place-items-center w-9 h-9 rounded-full bg-[#C2410C] text-ivory hover:bg-windsor-dark transition-colors"
            aria-label="Stop commentary"
          >
            <Pause size={16} />
          </button>
        </div>
      )}
    </CommentaryContext.Provider>
  );
};
